import { useCallback } from 'react';
import { Box, Button, IconButton, Stack, Typography, useTheme, useMediaQuery } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import NoteCard from '@/components/NoteCard/NoteCard';
import { useAuthStore } from '@/stores/authStore';
import { useDeleteNote } from '@/services/noteGoApi/noteGoApiComponents';
import useAlertStore from '@/stores/alertStore';
import { useNavigate } from 'react-router';
import { RouteMap } from '@/routes/Routes';
import wavesGif from '@/assets/waves.gif';
import SkeletonCard from '@/components/NoteCard/SkeletonCard';
import { useExtensionManager } from '@/hooks/useExtensionManager';
import NewNoteModal from '@/components/CustomModal/NewNoteModal';
import { useAuthProtection } from '@/features/auth/useAuthProtection';
import { getDeleteNoteErrorMessage } from '@/utils/deleteNoteError';

interface HomeListProps {
  userHaveNotes: boolean | null;
  notes: NoteOut[] | undefined;
  refetchNotes: () => void;
  isNotesLoading?: boolean;
}

const HomeList = ({ userHaveNotes, notes, refetchNotes, isNotesLoading }: HomeListProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // 900px and below
  const { userToken } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);
  const { mutateAsync: deleteNote } = useDeleteNote();
  const navigate = useNavigate();
  const { isModalOpen, checkExtensionInstalled, onModalConfirm, setIsModalOpen } = useExtensionManager();
  const { canCreateNote } = useAuthProtection();

  const handleDelete = useCallback(
    async (noteId: string) => {
      if (!noteId) return;
      try {
        await deleteNote(
          {
            headers: { authorization: `Bearer ${userToken}` },
            pathParams: { noteId },
          },
          {
            onSuccess: () => {
              refetchNotes();
              showAlert('Note deleted successfully.', 'success');
            },
            onError: (error) => {
              showAlert(getDeleteNoteErrorMessage(error), 'error');
            },
          },
        );
      } catch {
        // mutateAsync always rejects on failure — onError above handles user feedback.
      }
    },
    [deleteNote, userToken, refetchNotes, showAlert],
  );

  const renderEmptyList = () => {
    return (
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '280px',
          bgcolor: '#ECEFF1',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          px: 3,
          py: 4,
        }}
      >
        <Box mb={3}>
          <Typography
            variant="h5"
            sx={{
              color: '#607D8B',
              textAlign: 'center',
              fontWeight: 700,
            }}
          >
            Let's get started!
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: '#607D8B',
              textAlign: 'center',
              fontWeight: 400,
            }}
          >
            You haven't created any notes yet.
          </Typography>
        </Box>

        <IconButton
          sx={{
            bgcolor: '#607D8B',
            color: 'white',
            '&:hover': {
              bgcolor: 'grey.500',
            },
            width: 48,
            height: 48,
          }}
          onClick={canCreateNote ? checkExtensionInstalled : () => navigate(`${RouteMap.PLAN}#${RouteMap.PLAN_ANCHOR}`)}
        >
          <ArrowForwardIcon />
        </IconButton>

        <Box
          component="img"
          src={wavesGif}
          alt=""
          sx={{
            position: 'absolute',
            bottom: -120,
            width: '876px',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 0,
            opacity: 1,
            mixBlendMode: 'screen',
          }}
        />
      </Box>
    );
  };

  const renderNotesList = () => {
    return (
      <>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: isMobile ? 2 : 3,
            width: '100%',
          }}
          pb={2}
        >
          {notes?.map(
            ({
              id,
              title,
              tags,
              transcript_url,
              video_url,
              created_at,
              processing_type,
              thumbnail_card_url,
              thumbnail_url,
            }) => (
              <NoteCard
                key={id}
                noteId={id}
                title={title || 'Untitled'}
                tags={tags}
                transcript={transcript_url ? transcript_url : ''}
                onConfirmDelete={handleDelete}
                hasVideo={!!video_url}
                createdAt={created_at}
                processingType={processing_type}
                thumbnailUrl={thumbnail_card_url || thumbnail_url || ''}
              />
            ),
          )}
        </Box>
        <Button
          variant="text"
          color="secondary"
          onClick={() => {
            navigate(RouteMap.MY_NOTES);
          }}
        >
          View full list
        </Button>
      </>
    );
  };

  // Show skeleton while loading or when userHaveNotes state is still null
  if (isNotesLoading || userHaveNotes === null) {
    const skeletonCount = 6;
    return (
      <Stack maxWidth={isMobile ? '100%' : 1620} width="100%" alignItems={'flex-start'}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: isMobile ? 2 : 3,
            width: '100%',
          }}
          pb={2}
        >
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <SkeletonCard key={index} />
          ))}
        </Box>
      </Stack>
    );
  }

  return (
    <Stack maxWidth={isMobile ? '100%' : 1620} width="100%" alignItems={userHaveNotes ? 'flex-start' : 'center'}>
      {userHaveNotes ? renderNotesList() : renderEmptyList()}
      <NewNoteModal open={isModalOpen} handleClose={() => setIsModalOpen(false)} onConfirm={onModalConfirm} />
    </Stack>
  );
};

export default HomeList;
